import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  MagnifyingGlass,
  FunnelSimple,
  ChartBar,
  ForkKnife,
  CookingPot,
  Receipt,
  BookmarkSimple,
  CaretRight,
  Camera,
  ShareNetwork,
  CircleNotch,
  PencilSimple,
  Trash,
  X,
  Image as ImageIcon,
} from "@phosphor-icons/react";
import { toast } from "sonner";
import api from "../../lib/api";
import { getUserId } from "../../lib/user";
import { confirmDelete } from "../../lib/confirmDelete";
import { formatMoney, getHomeCurrency } from "../../lib/currency";
import { getCountry } from "../../lib/tipping";
import {
  getWatermarkLevel,
  getWatermarkOpacity,
} from "../../lib/watermarkSettings";
import { shareTipCard } from "../../lib/shareTipCard";
import { resizeToDataUrl } from "../../lib/imageUtils";
import ShareStickerModal from "./ShareStickerModal";
import BrandMark from "./BrandMark";
import AccountButton from "../AccountButton";

const FILTERS = [
  { id: "all", label: "All" },
  { id: "recent", label: "Recent" },
  { id: "country", label: "By Country" },
  { id: "notes", label: "Notes" },
];

/**
 * Saved tab — Tip Log redesigned per v2 mockup.
 *
 * Features:
 *   • Stats summary card (total tipped + this month)
 *   • Search across restaurant/note/place
 *   • Filter chips (All / Recent / By Country / Notes)
 *   • Beautiful per-entry log cards w/ flag, badge, big total
 */
export default function SavedTab({ openPaywall, onNavigate }) {
  const [log, setLog] = useState([]);
  const [loading, setLoading] = useState(true);
  const [query, setQuery] = useState("");
  const [filter, setFilter] = useState("all");

  const userId = useMemo(() => getUserId(), []);
  const homeCurrency = getHomeCurrency();

  useEffect(() => {
    let live = true;
    (async () => {
      setLoading(true);
      try {
        const r = await api.get(`/tips`, { params: { user_id: userId } });
        if (live) setLog(r.data.items || []);
      } catch (err) {
        toast.error("Couldn't load tip log");
      } finally {
        if (live) setLoading(false);
      }
    })();
    return () => {
      live = false;
    };
  }, [userId]);

  const stats = useMemo(() => {
    const totals = { count: log.length, totalTipped: 0, monthTipped: 0, monthCount: 0 };
    const now = new Date();
    const cutoff = new Date(now.getFullYear(), now.getMonth(), 1);
    for (const e of log) {
      const tipInHome =
        Number.isFinite(e.home_rate) && e.home_rate > 0
          ? (e.tip_amount || 0) * e.home_rate
          : e.tip_amount || 0;
      totals.totalTipped += tipInHome;
      const d = e.created_at ? new Date(e.created_at) : null;
      if (d && d >= cutoff) {
        totals.monthTipped += tipInHome;
        totals.monthCount += 1;
      }
    }
    return totals;
  }, [log]);

  const filtered = useMemo(() => {
    let arr = [...log];
    if (filter === "recent") {
      arr.sort((a, b) => new Date(b.created_at) - new Date(a.created_at));
      arr = arr.slice(0, 10);
    } else if (filter === "country") {
      arr.sort((a, b) => (a.country_code || "").localeCompare(b.country_code || ""));
    } else if (filter === "notes") {
      arr = arr.filter((e) => e.note && e.note.trim().length > 0);
    }
    if (query.trim()) {
      const q = query.trim().toLowerCase();
      arr = arr.filter((e) => {
        const countryName = e.country_code ? (getCountry(e.country_code)?.name || "") : "";
        const hay = `${e.note || ""} ${e.currency || ""} ${e.country_code || ""} ${countryName}`.toLowerCase();
        return hay.includes(q);
      });
    }
    return arr;
  }, [log, filter, query]);

  return (
    <div className="px-5 pt-4" data-testid="saved-screen">
      {/* Top bar */}
      <div className="flex items-center justify-between mb-5">
        <BrandMark />
        <AccountButton openPaywall={() => openPaywall?.()} />
      </div>

      {/* Hero title */}
      <h1 className="font-jakarta text-5xl font-extrabold leading-none tracking-tight text-navy mb-5">
        Tip <span className="font-jakarta font-extrabold text-orange">Log</span>
      </h1>

      {/* Search bar */}
      <div className="bg-white rounded-2xl shadow-soft border border-navy/5 flex items-center gap-2 pl-2 pr-3 py-2 mb-4">
        <button
          type="button"
          aria-label="Search"
          className="w-10 h-10 rounded-full bg-navy flex items-center justify-center"
        >
          <MagnifyingGlass size={18} weight="bold" color="#fff" />
        </button>
        <input
          data-testid="saved-search-input"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder="Search restaurants, notes, or places…"
          className="flex-1 bg-transparent text-base font-medium text-navy placeholder:text-navy-soft outline-none"
        />
        <button
          type="button"
          aria-label="Filter"
          className="w-10 h-10 rounded-xl flex items-center justify-center text-navy-soft"
        >
          <FunnelSimple size={18} weight="bold" />
        </button>
      </div>

      {/* Stats card */}
      <button
        type="button"
        onClick={() => onNavigate?.("trips")}
        data-testid="stats-card"
        className="w-full bg-white rounded-2xl shadow-soft border border-navy/5 p-4 flex items-center gap-4 mb-4 text-left hover:shadow-float transition-shadow"
      >
        <div className="shrink-0 w-12 h-12 rounded-2xl bg-sky-soft flex items-center justify-center">
          <ChartBar size={24} weight="duotone" color="#2A6FD8" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="text-navy font-semibold text-base leading-tight">
            Your tipping summary
          </div>
          <div className="text-navy-soft text-sm mt-0.5">
            {stats.count} saved {stats.count === 1 ? "log" : "logs"}
          </div>
          <div className="text-forest font-bold text-base mt-1">
            {formatMoney(stats.totalTipped, homeCurrency)}{" "}
            <span className="text-navy-soft font-medium text-sm">total tipped</span>
          </div>
        </div>
        <div className="shrink-0 text-right border-l border-navy/10 pl-4">
          <div className="text-navy-soft text-[10px] uppercase tracking-wider font-semibold">
            This month
          </div>
          <div className="text-navy font-bold text-lg leading-tight mt-0.5">
            {formatMoney(stats.monthTipped, homeCurrency)}
          </div>
          <div className="text-navy-soft text-xs">{stats.monthCount} logs</div>
        </div>
        <CaretRight size={18} weight="bold" color="#4A5D78" />
      </button>

      {/* Filter chips */}
      <div className="flex gap-2 overflow-x-auto no-scrollbar mb-4 -mx-5 px-5">
        {FILTERS.map((f) => {
          const active = filter === f.id;
          return (
            <button
              key={f.id}
              data-testid={`filter-${f.id}`}
              onClick={() => setFilter(f.id)}
              className={`shrink-0 px-4 py-2 rounded-full text-sm font-semibold border transition-colors ${
                active
                  ? "bg-navy text-white border-navy"
                  : "bg-white text-navy border-navy/10 hover:border-navy/30"
              }`}
            >
              {f.id === "recent" && "🕐 "}
              {f.id === "country" && "🌐 "}
              {f.id === "notes" && "🔖 "}
              {f.label}
            </button>
          );
        })}
      </div>

      {/* Log list */}
      {loading && (
        <div className="text-center text-navy-soft py-12 text-sm">
          Loading your tip log…
        </div>
      )}

      {!loading && filtered.length === 0 && (
        <div className="text-center bg-white rounded-2xl shadow-soft border border-navy/5 py-10 px-6">
          <div className="text-navy font-jakarta font-extrabold text-xl mb-1">
            No tips yet
          </div>
          <div className="text-navy-soft text-sm">
            Save your first tip from the Home tab.
          </div>
        </div>
      )}

      <div className="space-y-3 pb-4">
        {filtered.map((e, idx) => (
          <LogCard
            key={e.id}
            entry={e}
            altRing={idx % 2 === 1}
            userId={userId}
            onDeleted={(id) => setLog((prev) => prev.filter((x) => x.id !== id))}
            onUpdated={(updated) =>
              setLog((prev) => prev.map((x) => (x.id === updated.id ? updated : x)))
            }
          />
        ))}
      </div>

      {/* Bottom promo CTA */}
      <div className="mt-2 bg-sky-soft rounded-2xl p-4 flex items-center gap-4">
        <div className="shrink-0 w-12 h-12 rounded-2xl bg-navy flex items-center justify-center">
          <Receipt size={22} weight="duotone" color="#fff" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="font-semibold text-navy text-base leading-tight">
            Keep every great experience
          </div>
          <div className="text-navy-soft text-sm leading-snug mt-0.5">
            Scan receipts and add notes so you never forget the details.
          </div>
        </div>
        <button
          type="button"
          data-testid="saved-scan-receipt-btn"
          onClick={() => onNavigate?.("home:scan-receipt")}
          className="shrink-0 bg-white border border-navy/10 rounded-full px-3 py-2 text-xs font-semibold text-navy flex items-center gap-1.5"
        >
          <Camera size={14} weight="duotone" /> Scan receipt
        </button>
      </div>
    </div>
  );
}

// Country codes with a hand-painted landmark JPG available in
// /assets/landmarks/. Mirrors the LANDMARK_IMAGE_CODES set in
// /components/v2/illustrations.jsx and is used by the SavedTab LogCard
// to render the country's landmark as a soft watermark.
const LANDMARK_CODES_WITH_ART = new Set([
  "US","CA","MX","CR","PA","GT","BR","AR","CL","CO","PE","EC","UY","VE","BS","JM","DO",
  "PR","BB","KY","TT","AW",
  "GB","FR","IT","ES","DE","NL","CH","SE","NO","DK","AT","BE","PT","GR","TR","PL","CZ","HU",
  "IE","FI","IS","LU","HR","RO","BG","UA","RU","SK","SI","EE","LV","LT",
  "JP","KR","CN","HK","TW","SG","TH","VN","ID","PH",
  "MY","IN","PK","BD","LK","NP","KH","LA","MM","AU","NZ","FJ",
  "SA","QA","KW","BH","OM","JO","LB","IL","EG","MA","ZA","KE","NG","ET","AE",
]);

function LogCard({ entry, altRing, userId, onDeleted, onUpdated }) {
  const [sharing, setSharing] = useState(false);
  const [pickingSticker, setPickingSticker] = useState(false);
  const [expanded, setExpanded] = useState(false);
  const [editing, setEditing] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [addingPhoto, setAddingPhoto] = useState(false);
  const photoInputRef = useRef(null);
  const country = entry.country_code ? getCountry(entry.country_code) : null;
  const flag = country?.flag || "🌍";
  const cc = entry.country_code || "—";
  const date = entry.created_at
    ? new Date(entry.created_at).toLocaleDateString(undefined, {
        month: "short",
        day: "numeric",
        year: "numeric",
      })
    : "";
  const localCurrency = entry.currency || "USD";
  const rate = Number.isFinite(entry.home_rate) ? entry.home_rate : null;
  const homeCurrency = entry.home_currency || "USD";
  const totalLocal = entry.total ?? 0;
  const showHome = rate && localCurrency !== homeCurrency;
  const totalHome = showHome ? totalLocal * rate : null;
  const Icon = altRing ? CookingPot : ForkKnife;
  const ringClass = altRing
    ? "bg-forest-soft text-forest"
    : "bg-orange-soft text-orange";
  const hasLandmarkArt = entry.country_code && LANDMARK_CODES_WITH_ART.has(entry.country_code);
  const watermarkOpacity = getWatermarkOpacity(getWatermarkLevel());
  const showWatermark = hasLandmarkArt && watermarkOpacity > 0;

  async function handleDelete(e) {
    e.stopPropagation();
    if (!(await confirmDelete())) return;
    setDeleting(true);
    try {
      await api.delete(`/tips/${entry.id}`, { params: { user_id: userId } });
      onDeleted?.(entry.id);
      toast.success("Tip deleted");
    } catch {
      toast.error("Couldn't delete this tip");
    } finally {
      setDeleting(false);
    }
  }

  async function handleAddPhoto(e) {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    if (!/^image\//i.test(file.type) && !/\.(jpe?g|png|webp|heic|heif)$/i.test(file.name || "")) {
      toast.error("Please pick an image");
      return;
    }
    setAddingPhoto(true);
    try {
      // Aggressively downscale so iOS photos (~12 MP HEIC→JPEG ≈ 3-5 MB raw)
      // fit comfortably inside the request body limits at every layer
      // (Cloudflare 100 MB, FastAPI default 1 MB JSON body for /tips).
      const dataUrl = await resizeToDataUrl(file, 1000, 0.75);
      const r = await api.patch(`/tips/${entry.id}`, {
        user_id: userId,
        food_photo_base64: dataUrl,
      });
      onUpdated?.(r.data);
      toast.success("Photo added to your tip");
    } catch (err) {
      // Surface the actual reason so the user knows what to fix. The most
      // common failures are: (a) iOS HEIC bypass, (b) >413 payload from a
      // huge image, (c) /tips PATCH 404 because the local tip id was created
      // before sign-in and isn't in the cloud.
      // eslint-disable-next-line no-console
      console.error("[food-photo] upload failed", err);
      const code = err?.code;
      const status = err?.response?.status;
      const detail = err?.response?.data?.detail;
      if (code === "UNSUPPORTED_FORMAT" || code === "DECODE_FAILED") {
        toast.error(err.message);
      } else if (status === 413) {
        toast.error("Photo too large — try a smaller image");
      } else if (status === 404) {
        toast.error("This tip is no longer in your cloud log — try refreshing");
      } else if (detail) {
        toast.error(detail);
      } else {
        toast.error("Couldn't attach photo");
      }
    } finally {
      setAddingPhoto(false);
    }
  }

  return (
    <div
      role="button"
      tabIndex={0}
      onClick={() => setExpanded((v) => !v)}
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          setExpanded((v) => !v);
        }
      }}
      data-testid="log-card"
      aria-expanded={expanded}
      className="w-full text-left bg-white rounded-2xl shadow-soft border border-navy/5 p-4 flex gap-3 relative overflow-hidden active:bg-bone/30 transition-colors cursor-pointer focus:outline-none focus:ring-2 focus:ring-navy/30"
    >
      {showWatermark && (
        <div
          className="absolute inset-y-0 right-0 w-1/2 pointer-events-none flex items-center justify-end"
          aria-hidden
        >
          <img
            src={`/assets/landmarks/${entry.country_code}.jpg`}
            alt=""
            loading="lazy"
            className="h-[140%] w-auto object-contain"
            style={{ opacity: watermarkOpacity }}
          />
        </div>
      )}
      <div
        className={`shrink-0 w-12 h-12 rounded-2xl ${ringClass} flex items-center justify-center relative z-10`}
      >
        {entry.food_photo_base64 ? (
          <img
            src={
              entry.food_photo_base64.startsWith("data:")
                ? entry.food_photo_base64
                : `data:image/jpeg;base64,${entry.food_photo_base64}`
            }
            alt=""
            className="w-12 h-12 rounded-2xl object-cover"
          />
        ) : (
          <Icon size={22} weight="fill" />
        )}
      </div>
      <div className="flex-1 min-w-0 relative z-10">
        <div className="flex items-center gap-2 text-navy-soft text-xs font-semibold mb-0.5">
          <span className="text-base leading-none">{flag}</span>
          <span>{country?.name || cc}</span>
          <span aria-hidden>•</span>
          <span>{localCurrency}</span>
        </div>
        <div className={`text-navy font-bold text-base leading-tight ${expanded ? "" : "truncate"}`}>
          {entry.note ? deriveTitle(entry.note) : `${country?.name || cc} tip`}
        </div>
        {entry.note && noteTag(entry.note) && (
          <div className="inline-block bg-forest-soft text-forest text-xs font-semibold rounded-full px-2 py-0.5 mt-1">
            {noteTag(entry.note)}
          </div>
        )}
        <div className="text-navy-soft text-xs mt-1">
          Bill {formatMoney(entry.bill || 0, localCurrency)} • {entry.tip_pct ?? "—"}%
          • {entry.people || 1} {(entry.people || 1) === 1 ? "person" : "people"}
        </div>

        {expanded && (
          <div className="mt-3 pt-3 border-t border-navy/10 space-y-2.5">
            {entry.food_photo_base64 && (
              <img
                src={
                  entry.food_photo_base64.startsWith("data:")
                    ? entry.food_photo_base64
                    : `data:image/jpeg;base64,${entry.food_photo_base64}`
                }
                alt="Food"
                data-testid="log-card-food-photo"
                className="w-full max-h-56 object-cover rounded-xl border border-navy/10"
              />
            )}
            {entry.note && (
              <div data-testid="log-card-full-note" className="text-navy text-sm leading-relaxed font-jakarta font-medium">
                &ldquo;{entry.note}&rdquo;
              </div>
            )}
            <div className="grid grid-cols-3 gap-2 mt-2">
              <BreakdownStat label="Bill" value={formatMoney(entry.bill || 0, localCurrency)} />
              <BreakdownStat
                label="Tip"
                value={
                  entry.tip_amount != null
                    ? formatMoney(entry.tip_amount, localCurrency)
                    : "—"
                }
                accent="orange"
              />
              <BreakdownStat
                label="Per person"
                value={
                  entry.per_person != null
                    ? formatMoney(entry.per_person, localCurrency)
                    : formatMoney(
                        (entry.total || 0) / Math.max(1, entry.people || 1),
                        localCurrency
                      )
                }
              />
            </div>
            {totalHome && (
              <div className="text-navy-soft text-xs">
                In home currency: ≈ {formatMoney(totalHome, homeCurrency)}{" "}
                <span className="opacity-60">
                  (rate {Number(rate).toFixed(4)} {homeCurrency}/{localCurrency})
                </span>
              </div>
            )}

            {/* Action row: Add Photo / Edit / Delete */}
            <div className="flex flex-wrap gap-2 pt-1">
              {!entry.food_photo_base64 && (
                <button
                  type="button"
                  data-testid="add-food-photo-btn"
                  disabled={addingPhoto}
                  onClick={(e) => {
                    e.stopPropagation();
                    photoInputRef.current?.click();
                  }}
                  className="flex-1 min-w-[110px] bg-white border border-navy/10 rounded-full px-3 py-2 text-xs font-jakarta font-bold text-navy flex items-center justify-center gap-1.5 active:bg-bone/60 disabled:opacity-60"
                >
                  {addingPhoto ? (
                    <CircleNotch size={14} weight="bold" className="animate-spin" />
                  ) : (
                    <ImageIcon size={14} weight="duotone" />
                  )}
                  Add photo
                </button>
              )}
              {entry.food_photo_base64 && (
                <button
                  type="button"
                  data-testid="replace-food-photo-btn"
                  disabled={addingPhoto}
                  onClick={(e) => {
                    e.stopPropagation();
                    photoInputRef.current?.click();
                  }}
                  className="flex-1 min-w-[110px] bg-white border border-navy/10 rounded-full px-3 py-2 text-xs font-jakarta font-bold text-navy flex items-center justify-center gap-1.5 active:bg-bone/60 disabled:opacity-60"
                >
                  <ImageIcon size={14} weight="duotone" /> Replace photo
                </button>
              )}
              <button
                type="button"
                data-testid="edit-tip-btn"
                onClick={(e) => {
                  e.stopPropagation();
                  setEditing(true);
                }}
                className="flex-1 min-w-[100px] bg-white border border-navy/10 rounded-full px-3 py-2 text-xs font-jakarta font-bold text-navy flex items-center justify-center gap-1.5 active:bg-bone/60"
              >
                <PencilSimple size={14} weight="bold" /> Edit
              </button>
              <button
                type="button"
                data-testid="delete-tip-btn"
                disabled={deleting}
                onClick={handleDelete}
                className="flex-1 min-w-[100px] bg-orange-soft border border-orange/20 rounded-full px-3 py-2 text-xs font-jakarta font-bold text-orange flex items-center justify-center gap-1.5 active:bg-orange/15 disabled:opacity-60"
              >
                {deleting ? (
                  <CircleNotch size={14} weight="bold" className="animate-spin" />
                ) : (
                  <Trash size={14} weight="bold" />
                )}
                Delete
              </button>
            </div>
            <div className="text-navy-soft text-[11px] uppercase tracking-wider font-bold">
              Tap card to collapse
            </div>
          </div>
        )}
      </div>
      {/* Hidden file input lives OUTSIDE the conditional so it remains mounted
          while iOS WKWebView is suspended during native camera capture.
          Unmounting mid-capture causes the resulting File to be dropped,
          which is exactly the "photo getting lost" bug reported by the user. */}
      <input
        ref={photoInputRef}
        type="file"
        accept="image/*"
        capture="environment"
        className="hidden"
        onChange={handleAddPhoto}
        data-testid="food-photo-input"
      />
      <div className="shrink-0 text-right">
        <div className="text-navy-soft text-[10px] uppercase tracking-wider mb-0.5">
          {date}
        </div>
        <div className="font-jakarta font-extrabold text-navy text-xl leading-none">
          {formatMoney(totalLocal, localCurrency)}
        </div>
        {totalHome && (
          <div className="text-navy-soft text-xs mt-0.5">
            ≈ {formatMoney(totalHome, homeCurrency)}
          </div>
        )}
        <div className="flex justify-end items-center gap-2 mt-2">
          <button
            type="button"
            data-testid="share-tip-btn"
            disabled={sharing}
            aria-label="Share this tip"
            onClick={(e) => {
              e.stopPropagation();
              if (sharing) return;
              setPickingSticker(true);
            }}
            className="bg-navy text-white rounded-full px-3 py-1.5 flex items-center gap-1.5 font-jakarta font-extrabold text-xs shadow-soft active:scale-95 transition-transform disabled:opacity-60"
          >
            {sharing ? (
              <CircleNotch size={14} weight="bold" className="animate-spin" />
            ) : (
              <ShareNetwork size={14} weight="bold" />
            )}
            Share
          </button>
        </div>
      </div>

      {editing && (
        <TipEditModal
          entry={entry}
          userId={userId}
          onClose={() => setEditing(false)}
          onSaved={(updated) => {
            onUpdated?.(updated);
            setEditing(false);
            toast.success("Tip updated");
          }}
        />
      )}

      {pickingSticker && (
        <ShareStickerModal
          entry={entry}
          sharing={sharing}
          onClose={() => setPickingSticker(false)}
          onShare={async (stickerId, opts = {}) => {
            if (sharing) return;
            setSharing(true);
            try {
              const res = await shareTipCard(entry, {
                sticker: stickerId,
                hideAmounts: !!opts.hideAmounts,
                frame: opts.frame || null,
                stickerPos: opts.stickerPos || null,
                intent: opts.intent,
              });
              if (res?.method === "download") {
                toast.success("Saved tip image to your downloads");
              } else if (res?.method === "cancelled") {
                // user dismissed the native share sheet — no toast
              }
              setPickingSticker(false);
            } catch {
              toast.error("Couldn't render share image");
            } finally {
              setSharing(false);
            }
          }}
        />
      )}
    </div>
  );
}

function TipEditModal({ entry, userId, onClose, onSaved }) {
  const [bill, setBill] = useState(String(entry.bill ?? ""));
  const [tipPct, setTipPct] = useState(String(entry.tip_pct ?? ""));
  const [people, setPeople] = useState(String(entry.people ?? 1));
  const [note, setNote] = useState(entry.note || "");
  const [saving, setSaving] = useState(false);

  async function submit(e) {
    e.preventDefault();
    const b = parseFloat(bill) || 0;
    const p = parseFloat(tipPct);
    const pp = Math.max(1, parseInt(people, 10) || 1);
    if (!Number.isFinite(p)) {
      toast.error("Tip % must be a number");
      return;
    }
    const tipAmount = b * (p / 100);
    const total = b + tipAmount;
    const perPerson = total / pp;
    setSaving(true);
    try {
      const r = await api.patch(`/tips/${entry.id}`, {
        user_id: userId,
        bill: b,
        tip_pct: p,
        tip_amount: tipAmount,
        people: pp,
        total,
        per_person: perPerson,
        note: note,
      });
      onSaved(r.data);
    } catch {
      toast.error("Couldn't update tip");
    } finally {
      setSaving(false);
    }
  }

  return (
    <div
      data-testid="tip-edit-modal"
      onClick={(e) => {
        e.stopPropagation();
        if (e.target === e.currentTarget) onClose();
      }}
      className="fixed inset-0 z-50 bg-navy/40 flex items-start justify-center p-3 pt-6 overflow-y-auto"
    >
      <form
        onClick={(e) => e.stopPropagation()}
        onSubmit={submit}
        className="w-full max-w-md bg-white rounded-3xl shadow-float p-5 space-y-4"
      >
        <div className="flex items-center justify-between">
          <h3 className="font-jakarta font-extrabold text-navy text-lg">Edit tip</h3>
          <button
            type="button"
            onClick={onClose}
            aria-label="Close"
            className="w-9 h-9 rounded-full bg-bone/70 flex items-center justify-center"
          >
            <X size={18} weight="bold" color="#0E2C54" />
          </button>
        </div>
        <EditField label="Bill" value={bill} onChange={setBill} testId="edit-bill" inputMode="decimal" />
        <EditField label="Tip %" value={tipPct} onChange={setTipPct} testId="edit-tip-pct" inputMode="decimal" />
        <EditField label="Party size" value={people} onChange={setPeople} testId="edit-people" inputMode="numeric" />
        <label className="block">
          <div className="text-navy-soft text-xs font-jakarta font-bold uppercase tracking-wider mb-1">
            Note
          </div>
          <textarea
            value={note}
            onChange={(e) => setNote(e.target.value)}
            data-testid="edit-note"
            rows={3}
            className="w-full bg-bone/70 border border-navy/10 rounded-xl px-3 py-2.5 text-base font-jakarta text-navy outline-none focus:border-orange/40 resize-none"
          />
        </label>
        <div className="flex gap-3 pt-1">
          <button
            type="button"
            onClick={onClose}
            className="flex-1 bg-white border border-navy/10 rounded-full py-3 font-jakarta font-bold text-navy"
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={saving}
            data-testid="edit-save-btn"
            className="flex-1 bg-orange text-white rounded-full py-3 font-jakarta font-extrabold disabled:opacity-60"
          >
            {saving ? "Saving…" : "Save changes"}
          </button>
        </div>
      </form>
    </div>
  );
}

function EditField({ label, value, onChange, testId, inputMode = "text" }) {
  return (
    <label className="block">
      <div className="text-navy-soft text-xs font-jakarta font-bold uppercase tracking-wider mb-1">
        {label}
      </div>
      <input
        inputMode={inputMode}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        data-testid={testId}
        className="w-full bg-bone/70 border border-navy/10 rounded-xl px-3 py-2.5 text-base font-jakarta font-semibold text-navy outline-none focus:border-orange/40"
      />
    </label>
  );
}

function BreakdownStat({ label, value, accent }) {
  const colorCls = accent === "orange" ? "text-orange" : "text-navy";
  return (
    <div className="bg-bone/60 rounded-xl px-2 py-2 text-center">
      <div className="text-navy-soft text-[9px] uppercase tracking-wider font-bold">
        {label}
      </div>
      <div className={`font-jakarta font-extrabold text-sm leading-tight mt-0.5 ${colorCls}`}>
        {value}
      </div>
    </div>
  );
}

// Derive a clean title from the multi-line note (first 60 chars before separator).
function deriveTitle(note) {
  const firstLine = note.split(/[\n·•—-]/)[0]?.trim() || note;
  return firstLine.length > 36 ? firstLine.slice(0, 36) + "…" : firstLine;
}

// If the note contains a tag-like badge, surface it.
function noteTag(note) {
  const lower = note.toLowerCase();
  if (lower.includes("excellent")) return "Excellent service";
  if (lower.includes("amazing")) return "Amazing";
  if (lower.includes("great service")) return "Great service";
  if (lower.includes("anniversary")) return "Anniversary 💞";
  if (lower.includes("birthday")) return "Birthday 🎂";
  if (lower.includes("graduation")) return "Graduation 🎓";
  return null;
}
